// Golf Match Play Tracker 2026 -- Schedule generation
//
// =====================================================================
// HOW A WEEK IS BUILT
//
// Each division has 8 players, so a round robin runs exactly 7 rounds --
// which is exactly the number of regular season dates. Every player
// faces all 7 of their division rivals once.
//
// A round of an 8-player round robin produces 4 pairs. A tee time holds
// 4 players. So each foursome is built from ONE pair out of division A
// and ONE pair out of division B:
//
//   Foursome = [A1, A2, B1, B2]  ->  A1 vs A2  and  B1 vs B2
//
// Pairing two divisions together gives 4 foursomes. Two division-pairs
// per week gives 8 foursomes = 32 players. Division pairings rotate
// weekly so every division shares tee times with every other division.
// =====================================================================

import { uid, teeTimeSlots } from "./helpers";
import { REGULAR_SEASON_ROUNDS, DEFAULT_TEE_TIME_PREFERENCE } from "./constants";

/**
 * Does this player want a late tee time?
 *
 * Anything that is not explicitly "late" counts as early, so a NULL
 * column (a player who never declared) behaves exactly as before.
 */
export function prefersLate(golfer) {
  const p = golfer?.tee_time_pref ?? DEFAULT_TEE_TIME_PREFERENCE;
  return String(p).toLowerCase() === "late";
}

/**
 * Order foursomes so that groups containing a late-preference player get
 * the LATEST tee times available.
 *
 * A tee time holds 4 players and two matches, so the preference has to be
 * resolved for the group as a whole. One late player sends the group late
 * -- there is no way to split them without breaking the round robin.
 *
 * Groups are sorted by:
 *   1. how many late players they hold (fewest first, so most-late lands last)
 *   2. their original round-robin position (stable, keeps pairings tidy)
 *
 * Because the caller assigns slots in array order, the last group in this
 * list receives the last tee time.
 *
 * @param groups   array of foursomes, each an array of golfer ids
 * @param golferMap Map(id -> golfer) so we can read the preference
 * @returns { ordered, lateGroupCount }
 */
export function orderGroupsByTeePreference(groups, golferMap) {
  const decorated = (groups || []).map((group, i) => {
    const lateCount = (group || []).reduce(
      (n, id) => n + (prefersLate(golferMap?.get?.(id)) ? 1 : 0), 0);
    return { group, i, lateCount };
  });

  decorated.sort((x, y) => (x.lateCount - y.lateCount) || (x.i - y.i));

  return {
    ordered: decorated.map(d => d.group),
    lateGroupCount: decorated.filter(d => d.lateCount > 0).length,
  };
}

/**
 * Circle-method round robin. Requires an even number of players.
 * Returns an array of rounds; each round is an array of [idA, idB] pairs.
 */
export function roundRobin(players) {
  const n = players.length;
  if (n < 2) return [];
  if (n % 2 !== 0) throw new Error("Round robin requires an even number of players");

  const fixed = players[0];
  let rest = players.slice(1);
  const rounds = [];

  for (let r = 0; r < n - 1; r++) {
    const line = [fixed, ...rest];
    const pairs = [];
    for (let i = 0; i < n / 2; i++) {
      pairs.push([line[i], line[n - 1 - i]]);
    }
    rounds.push(pairs);
    rest = [rest[rest.length - 1], ...rest.slice(0, rest.length - 1)];
  }
  return rounds;
}

/**
 * Verify a set of rounds is a true round robin: every player meets every
 * other player exactly once. Used by the Schedule tab's validator.
 */
export function validateRoundRobin(players, rounds) {
  const seen = new Map();
  players.forEach(p => seen.set(p, new Set()));
  const errors = [];

  rounds.forEach((pairs, ri) => {
    const inRound = new Set();
    pairs.forEach(([a, b]) => {
      if (inRound.has(a) || inRound.has(b)) {
        errors.push(`Round ${ri + 1}: a player appears twice`);
      }
      inRound.add(a); inRound.add(b);
      if (seen.get(a)?.has(b)) errors.push(`${a} plays ${b} more than once`);
      seen.get(a)?.add(b);
      seen.get(b)?.add(a);
    });
  });

  players.forEach(p => {
    const met = seen.get(p)?.size || 0;
    if (met !== players.length - 1) {
      errors.push(`${p} faces ${met} of ${players.length - 1} division rivals`);
    }
  });

  return { valid: errors.length === 0, errors };
}

/**
 * Build per-division round robins, keyed by division name.
 * @param golfersByDivision { Woods: [ids], Nicklaus: [ids], ... }
 */
export function divisionRoundRobins(golfersByDivision) {
  const out = {};
  Object.entries(golfersByDivision).forEach(([division, ids]) => {
    out[division] = roundRobin(ids);
  });
  return out;
}

/**
 * Generate every regular-season match for the season.
 *
 * @param opts.seasonDates  season_dates rows (phase 'regular'), each with
 *                          .assignments and .pairings
 * @param opts.golfersByDivision  { division: [golferId, ...] }
 * @param opts.golfers      full golfer rows -- only needed to honour the
 *                          early/late tee time preference. Omit and every
 *                          group is treated as early (original behaviour).
 * @returns array of match objects ready to upsert
 */
export function generateRegularSeason({ seasonDates, golfersByDivision, golfers }) {
  const gmap = new Map((golfers || []).map(g => [g.id, g]));
  const regularDates = (seasonDates || [])
    .filter(d => (d.phase || "regular") === "regular")
    .slice()
    .sort((a, b) => a.seq - b.seq);

  const rrs = divisionRoundRobins(golfersByDivision);
  const matches = [];
  const warnings = [];

  regularDates.forEach((date, weekIndex) => {
    if (weekIndex >= REGULAR_SEASON_ROUNDS) {
      warnings.push(`${date.label || date.play_date}: beyond round ${REGULAR_SEASON_ROUNDS}, the round robin has no more rounds. Skipped.`);
      return;
    }

    const pairings = date.pairings || [];
    if (pairings.length === 0) {
      warnings.push(`${date.label || date.play_date}: no division pairings set. Skipped.`);
      return;
    }

    // Build the foursomes for this week, keyed by division-pair index.
    // foursomesByPair[i] = array of [A1,A2,B1,B2]
    const foursomesByPair = pairings.map(([divA, divB]) => {
      const roundA = rrs[divA]?.[weekIndex];
      const roundB = rrs[divB]?.[weekIndex];
      if (!roundA || !roundB) {
        warnings.push(`${date.label}: missing round-robin data for ${divA} / ${divB}.`);
        return [];
      }
      const groups = [];
      for (let i = 0; i < Math.min(roundA.length, roundB.length); i++) {
        groups.push([roundA[i][0], roundA[i][1], roundB[i][0], roundB[i][1]]);
      }
      return groups;
    });

    // Assign foursomes to tee times. Each date_assignment names the
    // divisions playing that course, so on split weeks each course only
    // receives the foursomes belonging to its divisions.
    (date.assignments || []).forEach(asg => {
      const slots = teeTimeSlots(asg.first_tee_time, asg.interval_min || 10, asg.slot_count || 8);

      // Which division-pairs belong at this course?
      const wanted = (asg.divisions && asg.divisions.length)
        ? pairings
            .map((p, i) => ({ pair: p, i }))
            .filter(({ pair }) => pair.some(d => asg.divisions.includes(d)))
            .map(({ i }) => i)
        : pairings.map((_, i) => i);

      const rawQueue = wanted.flatMap(i => foursomesByPair[i] || []);

      // Honour the early/late tee time preference: groups holding a late
      // player sort to the end, so they take the latest slots.
      const { ordered: queue, lateGroupCount } =
        orderGroupsByTeePreference(rawQueue, gmap);

      // If the field overflows the available slots, the groups that get
      // dropped are the ones at the END of the queue -- which after the
      // sort above are the late players. Warn loudly, because silently
      // dropping the people who asked for a late time is the worst outcome.
      if (lateGroupCount > 0 && rawQueue.length > slots.length) {
        warnings.push(
          `${date.label || date.play_date} @ ${asg.course_id}: ${lateGroupCount} group(s) ` +
          `requested a late tee time but the field overflows the available slots, so ` +
          `late groups are the ones at risk of being dropped. Add tee times.`
        );
      }

      if (queue.length > slots.length) {
        warnings.push(
          `${date.label || date.play_date} @ ${asg.course_id}: ${queue.length} groups but only ` +
          `${slots.length} tee times (${asg.first_tee_time}, every ${asg.interval_min || 10} min). ` +
          `Increase slot count or add tee times.`
        );
      }

      queue.forEach((group, gi) => {
        if (gi >= slots.length) return;
        matches.push({
          id: uid("m"),
          season_date_id: date.id,
          match_date: date.play_date,
          tee_time: slots[gi],
          course_id: asg.course_id,
          nine: asg.nine || "F9",
          round_no: weekIndex + 1,
          phase: "regular",
          bracket_round: null,
          bracket_slot: null,
          group_players: group,
          pairings: group,     // read two at a time: [0]v[1], [2]v[3]
          results: Object.fromEntries(group.map(id => [id, "pending"])),
          golfer_selections: {},
          locked: false,
        });
      });
    });
  });

  return { matches, warnings };
}

/**
 * The three ways four divisions can be split into two pairs.
 * Used by the "rotate pairings" button in the Schedule tab.
 */
export function allDivisionPairings(divisions) {
  const [a, b, c, d] = divisions;
  return [
    [[a, b], [c, d]],
    [[a, c], [b, d]],
    [[a, d], [b, c]],
  ];
}

/**
 * Rotate division pairings across the season.
 *
 * Constraint honoured: on split-course weeks, Woods and Palmer must be at
 * DIFFERENT courses. Because each division-pair is assigned to one course,
 * that means Woods and Palmer must not be paired with each other on a
 * split week.
 */
export function rotatePairings(seasonDates, divisions, keepApart = ["Woods", "Palmer"]) {
  const options = allDivisionPairings(divisions);
  const isLegalForSplit = (opt) =>
    !opt.some(pair => keepApart.every(d => pair.includes(d)));

  let cursor = 0;
  return seasonDates.map(date => {
    if ((date.phase || "regular") !== "regular") return date;

    let choice = null;
    for (let attempt = 0; attempt < options.length; attempt++) {
      const candidate = options[(cursor + attempt) % options.length];
      if (!date.split_courses || isLegalForSplit(candidate)) {
        choice = candidate;
        cursor = (cursor + attempt + 1) % options.length;
        break;
      }
    }
    return { ...date, pairings: choice || date.pairings };
  });
}

/**
 * Capacity check for a date: do the tee times fit the field?
 */
export function checkDateCapacity(date, golfersByDivision) {
  const issues = [];
  (date.assignments || []).forEach(asg => {
    const divs = (asg.divisions && asg.divisions.length)
      ? asg.divisions
      : Object.keys(golfersByDivision);
    const players = divs.reduce((n, d) => n + (golfersByDivision[d]?.length || 0), 0);
    const groupsNeeded = Math.ceil(players / 4);
    const slots = asg.slot_count || 0;
    if (groupsNeeded > slots) {
      issues.push(
        `${asg.course_id}: ${players} players need ${groupsNeeded} tee times, only ${slots} configured.`
      );
    }
  });
  return issues;
}
